import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import './LoginNoticeManager.css';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function LoginNoticeManager() {
    const navigate = useNavigate();
    const [notice, setNotice] = useState({ title: '', body: '' });
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [reload, setReload] = useState(0);

    useEffect(() => {
        document.body.classList.add('login-notice-page-bg');
        return () => document.body.classList.remove('login-notice-page-bg');
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        async function load() {
            try {
                const res = await fetch(`${API_BASE}/api/admin/login-notice`, {
                    headers: headers(), signal: controller.signal, cache: 'no-store'
                });
                if (!res.ok) throw new Error('공지를 불러오지 못했습니다. 다시 시도해 주세요.');
                const data = await res.json();
                if (!controller.signal.aborted) { setNotice(data); setLoaded(true); }
            } catch (err) {
                if (!controller.signal.aborted) setError('공지를 불러오지 못했습니다. 다시 시도해 주세요.');
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }
        load();
        return () => controller.abort();
    }, [reload]);

    function change(field, value) {
        setNotice(prev => ({ ...prev, [field]: value }));
        setStatus('');
        setError('');
    }

    async function save(event) {
        event.preventDefault();
        if (!loaded || loading || saving) return;
        setStatus('');
        setError('');
        if (!notice.title.trim() || notice.title.trim().length > 120 ||
            !notice.body.trim() || notice.body.trim().length > 10000) {
            setError('제목은 1~120자, 본문은 1~10,000자로 입력해 주세요.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/admin/login-notice`, {
                method: 'PUT', headers: { ...headers(), 'Content-Type': 'application/json' },
                body: JSON.stringify(notice)
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || '공지 저장에 실패했습니다.');
            setNotice(data);
            setStatus('공지가 저장되었습니다. 로그인 화면에 반영됩니다.');
        } catch (err) {
            setError(err.message || '공지 저장에 실패했습니다. 다시 시도해 주세요.');
        } finally { setSaving(false); }
    }

    return <div className="login-notice-page">
        <div className="top-right-button-group">
            <button onClick={() => navigate('/dashboard')} className="navigate-button">대시보드</button>
            <button onClick={() => navigate('/user')} className="navigate-button">평가단 관리</button>
            <button onClick={() => navigate('/site')} className="navigate-button">사이트 관리</button>
            <button onClick={() => navigate('/admin')} className="navigate-button">설문 관리</button>
            <button onClick={() => navigate('/responses')} className="navigate-button">응답 관리</button>
            <button onClick={() => navigate('/results')} className="navigate-button">통계 보기</button>
            <button onClick={() => navigate('/admin/login-notice')} className="navigate-button primary">로그인 공지 관리</button>
            <button onClick={() => { localStorage.removeItem('token'); navigate('/'); }} className="navigate-button logout-button">로그아웃</button>
        </div>
        <main className="login-notice-manager">
            <h2>로그인 공지 관리</h2>
            <p>로그인 화면에 표시할 공지의 제목과 본문을 수정하세요. 저장하면 로그인 화면을 새로 열 때 반영됩니다.</p>
            {loading && <p role="status">공지를 불러오는 중입니다.</p>}
            {error && <p className="login-notice-error" role="alert">{error}</p>}
            {!loading && !loaded && <button type="button" className="navigate-button" onClick={() => setReload(prev => prev + 1)}>다시 불러오기</button>}
            <form onSubmit={save}>
                <label htmlFor="notice-title">공지 제목</label>
                <input id="notice-title" name="title" value={notice.title} maxLength={120} required
                    disabled={!loaded || saving} onChange={event => change('title', event.target.value)} />
                <label htmlFor="notice-body">공지 본문</label>
                <textarea id="notice-body" name="body" value={notice.body} maxLength={10000} rows={14} required
                    disabled={!loaded || saving} onChange={event => change('body', event.target.value)} aria-describedby="notice-body-help" />
                <p id="notice-body-help">줄바꿈과 빈 줄이 그대로 표시됩니다.</p>
                <button type="submit" className="navigate-button primary" disabled={!loaded || loading || saving}>{saving ? '저장 중…' : '공지 저장'}</button>
            </form>
            {status && <p className="login-notice-success" role="status">{status}</p>}
        </main>
    </div>;
}
